import React, { useEffect, useState, useRef } from "react";
import { Avatar, List, Tag } from "antd";
import { useDebounce } from "use-debounce";
import Modal from "./modal/Modal";
import HSButton from "../custom/input/button/Button";
import HSInput from "../custom/input/input/Input";
import useHttp from "../../hooks/useHttp";
import { ModalEmptyListView } from "../../pages/utils/CommonVessels";

const NewConversationModal = ({
  isOpen,
  onClose,
  appProperties,
  chatView,
  handleSelectNewContact,
  handleNumberSelect,
  setNewFromNumber,
  setShowChatOverlay,
  messageBodyRef
}) => {
  const [searchContact, setSearchContact] = useState("");
  const [contactList, setContactList] = useState([]);
  const [searchDropdownLoading, setSearchDropdownLoading] = useState(null);
  const [debouncedSearchContact] = useDebounce(searchContact, 500);
  const searchAbortController = useRef(null);
  const newToNumberInputRef = useRef(null);
  const { fetchData } = useHttp();

  useEffect(() => {
    if (!isOpen) {
      setSearchContact("");
      setContactList([]);
    }
  }, [isOpen]);

  useEffect(() => {
    const trimmedSearch = debouncedSearchContact?.trim() || "";

    if (searchAbortController.current) {
      searchAbortController.current.abort();
    }

    if (trimmedSearch.length > 2 && appProperties?.serviceInstalled) {
      searchAbortController.current = new AbortController();

      setSearchDropdownLoading(<div className="actionIconsSprite search-loading"></div>);
      fetchData(
        "omessage/" + appProperties?.randomIntegId + "/search?searchText=" + encodeURIComponent(trimmedSearch) + "&module=ALL",
        "GET",
        null,
        appProperties,
        searchAbortController.current.signal
      )
        .then(function (response) {
          try {
            if (response) {
              response = JSON.parse(response);
              const data = response?.data?.data || [];
              setContactList(data);
            }
            setSearchDropdownLoading(null);
          } catch (error) {
            console.error("Error processing response:", error);
            setSearchDropdownLoading(null);
          }
        })
        .catch((error) => {
        setSearchDropdownLoading(null);
        console.error(error);
      });
    } else {
      setContactList([]);
      setSearchDropdownLoading(null);
    }

    return () => {
      if (searchAbortController.current) {
        searchAbortController.current.abort();
      }
    };
  }, [debouncedSearchContact, appProperties?.serviceInstalled, appProperties?.randomIntegId, fetchData, appProperties]);

  const getFirstLetter = (name) => {
    if (!name) return "#";
    return name.charAt(0).toUpperCase();
  };
  const trimmedSearch = debouncedSearchContact?.trim() || "";
  const hasSearchInput = trimmedSearch.length > 2;
  const hasResults = contactList && contactList.length > 0;
  const isPhoneNumber = /^[+\d\s()-]+$/.test(trimmedSearch);
  const showNoResults = hasSearchInput && !hasResults && !searchDropdownLoading;

  return (
    <Modal
      open={isOpen}
      doOnClose={onClose}
      mask={true}
      header={<div className="pb-3 fw-semibold text-dark hs-fs-16">New Conversation</div>}
      modalCloseIcon={true}
      width={500}
      okButtonProps={null}
      cancelButtonProps={null}
      rootClassName="new-conversation-modal-wrapper"
      contentSpaceSize="small"
      headerDivider={<hr className="modal-header-divider mb-3" />}
      afterOpenChange={(open) => {
        if (open && newToNumberInputRef.current) {
          newToNumberInputRef.current.querySelector("input")?.focus();
        }
      }}
    >
      <div ref={newToNumberInputRef} className="d-flex align-items-center w-100 mb-3">
        <HSInput
          value={searchContact}
          placeholder="Enter name or phone..."
          onChange={(e) => setSearchContact(e.target.value)}
          rootClassName="new-conversation-modal-search-input"
          customClasses="w-100"
          size="m"
          variant="default"
          prefix={<div className="staticIconsSprite contactSearchIcon" />}
          suffix={searchDropdownLoading && <span>{searchDropdownLoading}</span>}
        />
      </div>

      <div className="overflow-scroll new-conv-modal-body-content">
        {!hasSearchInput && (
          <div className="d-flex flex-column align-items-center justify-content-center text-center h-100 py-5">
            <ModalEmptyListView
              icon={"staticIconsSprite searchEmptyTextIcon mb-3"}
              tagLineOne={
                <>
                  Start typing to search for existing
                  <br />
                  contacts or add a new one
                </>
              }
            />
          </div>
        )}

        {showNoResults && (
          <div className="new-conversation-modal-content-area d-flex flex-column align-items-center justify-content-center h-100 text-center">
            <ModalEmptyListView icon={"staticIconsSprite newContactAddAvatarIcon"} tagLineOne={`No Contact Found "${debouncedSearchContact}"`} />
            {isPhoneNumber && (
              <HSButton
                size="s"
                type="primary"
                prefixIcon={<div className="actionIconsSprite plusIconWhite" />}
                onClick={() => {
                  if (debouncedSearchContact !== "") {
                    setNewFromNumber(null);
                    handleNumberSelect(null, {
                      fromNumber: null,
                      toNumber: debouncedSearchContact,
                      new: true,
                    });
                    onClose();
                    setTimeout(() => {
                      messageBodyRef.current?.focus();
                    }, 600);
                    setShowChatOverlay(true);
                  }
                }}
                className="px-1 py-2 hs-fs-14"
                data-cy="add-new-user-in-inbox"
              >
                Add as New contact
              </HSButton>
            )}
          </div>
        )}

        {hasSearchInput && hasResults && (
          <div className="new-conversation-modal-content-area d-flex flex-column gap-2">
            {Array.from(new Set(contactList.map((obj) => obj?.module))).map((module) => (
              <div key={module || "no-module"} className="h-auto d-flex flex-column gap-1">
                {module && (
                  <Tag color="#F5F4FF" className="text-capitalize m-0 fw-medium hs-fs-12 hs-border-12 new-conv-modal-module-tag">
                    {module}
                  </Tag>
                )}

                <List
                  dataSource={contactList.filter((obj) => obj?.module === module)}
                  renderItem={(contact, index) => {
                    const displayName = ((contact?.firstName || "") + " " + (contact?.lastName || "")).trim() || "Unknown";
                    const phone = contact?.phone || "";

                    return (
                      <List.Item
                        key={`${module || "nomodule"}-${index}`}
                        onClick={() => {
                          handleSelectNewContact(contact);
                          onClose();
                          setTimeout(() => {
                            messageBodyRef.current?.focus();
                          }, 600);
                          setShowChatOverlay(true);
                        }}
                        className="p-2 my-1 rounded-2 cursor-pointer new-conv-contact-list-item"
                      >
                        <List.Item.Meta
                          className="d-flex align-items-end"
                          data-cy={`phone-${phone}`}
                          avatar={
                            contact?.avatar ? (
                              <Avatar src={contact.avatar} size={40} />
                            ) : (
                              <Avatar size={40} className="hs-bg-light-red hs-color-dark-orange hs-fw-600 hs-fs-16">
                                {getFirstLetter(displayName)}
                              </Avatar>
                            )
                          }
                          title={
                            <div className="fw-medium text-truncate text-dark hs-fs-14" style={{ lineHeight: 1 }}>
                              {displayName}
                            </div>
                          }
                          description={<div className="text-secondary text-truncate hs-fs-12">{phone}</div>}
                        />
                      </List.Item>
                    );
                  }}
                  split={false}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </Modal>
  );
};

export default NewConversationModal;
